import React, {useEffect} from 'react';
import * as PropTypes from 'prop-types';
import {useLocation, useNavigate} from 'react-router-dom';
import {initialState} from '../../../redux/initialState';
import './PrequalForm.scss';
import FooterNavigation, {closeEvent, compareStandardizedAddress} from '../../../components/FooterNavigation/FooterNavigation';
import PersonalInfoSection from './PersonalInfoSection/PersonalInfoSection';
import IncomeInfoSection from './IncomeInfoSection/IncomeInfoSection';
import {FormHeaderSection} from '../FormHeaderSection/FormHeaderSection';
import TimeOutSession from '../../../components/TimeOutSession/TimeOutSession';
import ResidenceInfoSection from './ResidenceInfoSection/ResidenceInfoSection';
import {
  updateAcceptedTermsAndConditions,
  updateApartment,
  updateCity,
  updateCurrentModalPage,
  updateDateOfBirth,
  updateEmail,
  updateHighestPageHit,
  updateIncome,
  updatePIDOBDay,
  updatePIDOBMonth,
  updatePIDOBYear,
  updatePIFirstName,
  updatePILastName,
  updatePostalCode,
  updateStateOrProvince,
  updateStreetAddress,
  validateCustomerData
} from '../../../redux/app-state-slice';
import {tagPrequalFormNextButtonClick} from '../../../Services/TaggingService/TaggingService';
import {checkIfFormIsValid} from '../../../validation/FormValidation';
import {DATA_FIELDS_ID} from '../constants';
import {trackEvents} from '../../../Services/OptimizelyService/OptimizelyService';
import APPROVED_DATA_CAN from '../../../utils/constants/APPROVED_DATA_CAN.json';
import CREDIT_FREEZE_DATA_CAN from '../../../utils/constants/CREDIT_FREEZE_DATA_CAN.json';
import QUEBEC_CREDIT_FREEZE_DATA_CAN from '../../../utils/constants/QUEBEC_CREDIT_FREEZE_DATA_CAN.json';
import APPROVED_DATA_USA from '../../../utils/constants/APPROVED_DATA_USA.json';
import CREDIT_FREEZE_DATA_USA from '../../../utils/constants/CREDIT_FREEZE_DATA_USA.json';
import {logService} from '../../../Services/LogService/LogService';
import {recaptchaChooser} from '../../../hooks/recaptchaChooser';

export const checkIfRecaptchaRuleIsEnabled = async (pathname, appConfig, customerData, dispatch) => {
  await recaptchaChooser(pathname, appConfig, customerData, dispatch);
};
function PrequalForm({dispatch, customerData, appConfig}) {
  const openModal = openModalAndUpdateCurrentModalPage();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const {FIRST_NAME, LAST_NAME, DOB, EMAIL, STREET_ADDRESS, STATE, APARTMENT, CITY, POSTAL_CODE, INCOME} = DATA_FIELDS_ID;
  const clickAction = () => {
    openModal();
  };

  const navigateToNextPage = async () => {
    if (customerData.showModalFlag.value) {
      dispatch(validateCustomerData(customerData));
    }
    if (checkIfFormIsValid(customerData)) {
      if (props.primaryButtonPath === '/review') {
        await compareAddressAndDetermineNavigation();
        tagPrequalFormNextButtonClick(customerData.optimizelyExperimentData, customerData.specialEvent.value);
      }
      checkIfRecaptchaRuleIsEnabled(pathname, appConfig, customerData, dispatch).then(() => {});
    } else {
      validateErrorAction();
    }
  };

  const props = {};
  props.primaryButtonLabel = 'Next';
  props.primaryButtonPath = '/review';
  props.primaryButtonTestId = customerData.isDealerHostApplication.value
    ? 'tier-3-footer-navigation-primary-button'
    : 'footer-navigation-primary-button';
  props.primaryButtonOnClickEvent = navigateToNextPage;
  props.closeButtonOnClickEvent = () => {
    closeEvent(customerData, dispatch);
  };
  props.buttonIsDisabled = checkIfFormIsValid(customerData);

  function openModalAndUpdateCurrentModalPage() {
    return () => {
      customerData.skipAddressStandardization.value
        ? dispatch(updateCurrentModalPage('/review-offer'))
        : dispatch(updateCurrentModalPage('/standardization'));
      trackEvents('reviewButtonEvent', customerData);
    };
  }

  function scrollToView(elementId) {
    const inputElement = document.getElementById(elementId);
    if (inputElement) {
      inputElement.focus();
      inputElement.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
    }
  }

  function validateErrorAction() {
    if (customerData.firstName.hasError) {
      scrollToView(FIRST_NAME);
    } else if (customerData.lastName.hasError) {
      scrollToView(LAST_NAME);
    } else if (customerData.email.hasError) {
      scrollToView(EMAIL);
    } else if (customerData.dateOfBirth.errorMessage.hasError) {
      scrollToView(DOB);
    } else if (customerData.streetAddress.hasError) {
      scrollToView(STREET_ADDRESS);
    } else if (customerData.apartment.hasError) {
      scrollToView(APARTMENT);
    } else if (customerData.city.hasError) {
      scrollToView(CITY);
    } else if (customerData.stateOrProvince.hasError) {
      scrollToView(STATE);
    } else if (customerData.postalCode.hasError) {
      scrollToView(POSTAL_CODE);
    } else if (customerData.income.hasError) {
      scrollToView(INCOME);
    }
  }

  async function compareAddressAndDetermineNavigation() {
    const standardizedAddress = await compareStandardizedAddress(customerData, appConfig);
    if (!customerData.skipAddressStandardization.value && !standardizedAddress) {
      clickAction();
    } else {
      if (props.primaryButtonLabel === 'Review') {
        trackEvents('reviewButtonEvent', customerData);
      }
      customerData.showModalFlag.value ? dispatch(updateCurrentModalPage(props.primaryButtonPath)) : navigate(props.primaryButtonPath);
    }
  }

  function logPrequalFormOpen() {
    let logInfo;
    if (customerData.isDealerHostApplication.value) {
      logInfo = {
        logType: 'TIER3_OPENING_MODAL',
        paCode: customerData.tier3PACode.value,
        prequalSource: customerData.prequalSource.value,
        vin: customerData.tier3Vin.value,
        countryCode: customerData.countryCode.value
      };
    } else {
      logInfo = {
        logType: 'TIER1_OPENING_MODAL',
        optimizelyExperimentData: customerData.optimizelyExperimentData,
        countryCode: customerData.countryCode.value
      };
    }
    logService(logInfo, appConfig)
      .then(() => {})
      .catch(() => {});
  }

  function setCustomerData(data) {
    dispatch(updatePIFirstName(data.firstName));
    dispatch(updatePILastName(data.lastName));
    dispatch(updatePIDOBDay(data.dobDay));
    dispatch(updatePIDOBMonth(data.dobMonth));
    dispatch(updatePIDOBYear(data.dobYear));
    dispatch(updateDateOfBirth(`${data.dobMonth}/${data.dobDay}/${data.dobYear}`));
    dispatch(updateEmail(data.email));
    dispatch(updateStreetAddress(data.homeAddress.streetAddress));
    if (!appConfig.modalUpdateFlag) {
      dispatch(updateApartment(data.homeAddress?.apartment));
    }
    dispatch(updateCity(data.homeAddress.city));
    dispatch(updatePostalCode(data.homeAddress.postalCode));
    dispatch(updateIncome(data.income));
    dispatch(updateAcceptedTermsAndConditions(true));
    dispatch(updateStateOrProvince(data.homeAddress.stateOrProvince));
  }

  function prepopulateTestDataForCanada() {
    if (appConfig.prepopulateApprovedCustomerTestDataCAN) {
      setCustomerData(APPROVED_DATA_CAN);
    } else if (appConfig.prepopulateCreditFreezeCustomerTestDataCAN) {
      setCustomerData(CREDIT_FREEZE_DATA_CAN);
    } else if (appConfig.prepopulateQuebecCreditFreezeCustomerTestDataCAN) {
      setCustomerData(QUEBEC_CREDIT_FREEZE_DATA_CAN);
    }
  }

  function prepopulateTestDataForUSA() {
    if (appConfig.prepopulateApprovedCustomerTestDataUSA) {
      setCustomerData(APPROVED_DATA_USA);
    } else if (appConfig.prepopulateCreditFreezeCustomerTestDataUSA) {
      setCustomerData(CREDIT_FREEZE_DATA_USA);
    }
  }

  useEffect(() => {
    logPrequalFormOpen();
    if (!customerData.isDealerHostApplication.value) {
      document.getElementById('prequal--web--form-section').scrollIntoView();
    }
    if (!customerData.showModalFlag.value) {
      dispatch(updateHighestPageHit(1));
    }
    if (customerData.countryCode.value === 'USA') {
      prepopulateTestDataForUSA();
    } else if (customerData.countryCode.value === 'CAN') {
      prepopulateTestDataForCanada();
    }
  }, []);

  return (
    <>
      <div id='prequal--web--form-section' className='prequal--wc--form-section'>
        <FormHeaderSection
          isDealerHostApplication
          dispatch={dispatch}
          customerData={customerData}
          navigate={navigate}
          headerText='Prequalify for financing in no time at all.'
        />
        <div
          className={`${customerData.isDealerHostApplication.value ? '' : 'prequal--web--form-section-container'} prequal--wc--form-section-container`}
          data-testid='form-section-container'
        >
          <div data-testid='form-section-header-dealer-host' className='prequal--wc--form-section-header-dealer-host-fomo'>
            <h1>Prequalify For Financing</h1>
            <div data-testid='form-section-subheader-dealer-host'>
              <h3 className='prequal--wc--form-section-subheader-dealer-host-fomo'>Zero credit impact, no obligation.</h3>
            </div>
          </div>
          <hr className={`prequal-form-line ${customerData.isDealerHostApplication.value ? 'prequal--wc--form-line' : ''}`} />
          <h2 className='prequal-form-section-title-fomo'>Personal Info</h2>
          <PersonalInfoSection dispatch={dispatch} customerData={customerData} />
          <div className='prequal-form-section-space' />
          <h2 className='prequal-form-section-title-fomo'>Residence Information</h2>
          <ResidenceInfoSection dispatch={dispatch} customerData={customerData} appConfig={appConfig} />
          <div className='prequal-form-section-space' />
          <h2 className='prequal-form-section-title-fomo'>Income</h2>
          <IncomeInfoSection dispatch={dispatch} customerData={customerData} />
        </div>
        <FooterNavigation props={props} customerData={customerData} />
      </div>
      <TimeOutSession appConfig={appConfig} dispatch={dispatch} customerData={customerData} navigate={navigate} />
    </>
  );
}

PrequalForm.propTypes = {
  dispatch: PropTypes.func,
  customerData: PropTypes.object,
  appConfig: PropTypes.object
};

PrequalForm.defaultProps = {
  customerData: initialState,
  dispatch: null,
  appConfig: null
};

export default PrequalForm;
